import React, { useState, useMemo, useEffect } from 'react';
import Language from './Languages';
import styled from 'styled-components';

// Import question components
import QuestionInformation from './QuestionInformation';
import QuestionText from './QuestionText';
import QuestionLikert from './QuestionLikert';
import QuestionMultiSelect from './QuestionMultiSelect';
import QuestionDropdown from './QuestionDropdown';
import QuestionFollowup from './QuestionFollowup';
import QuestionSlider from './QuestionSlider';
import FactorFollowUp from './FactorFollowUp';
import QuestionDate from './QuestionDate';
import QuestionAiFollowUp from './QuestionAIFollowup';

// Import styles
import {
  Container,
  Content,
  Progress,
  RedError,
  AnimatedCard,
  BottomBar,
  ButtonWrapper,
  ProgressWrapper,
  BackButton,
  NextButton,
  TopBanner
} from '../styles/SurveyStyles';
import { ChatInputContainer, ChatInput, SendButton, NextButtonWrapper } from "../styles/SurveyStyles";

const Logo = styled.img`
  height: 30px;
  position: absolute;
  top: 20px;
  left: 20px;
`;

const SurveyQuestions = ({
  themeColors,
  visibleQuestions,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  answers,
  handleAnswerChange,
  setCompleted,
  language,
  setLanguage,
  logo,
  sampleJson
}) => {
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const [chatDraft, setChatDraft] = useState("");
  const [shrinkNext, setShrinkNext] = useState(false);
  const [hideNextForText, setHideNextForText] = useState(false);

  // When arriving on a text question, animate input grow and shrink the Next button
  useEffect(() => {
    if (!currentQuestion) return;
    if (currentQuestion.type === "text") {
      setShowChatInput(true);
      setHideNextForText(false);
      setShrinkNext(true);
      const t = setTimeout(() => {
        setShrinkNext(false);
        setHideNextForText(true);
      }, 260);
      return () => clearTimeout(t);
    }
    // reset for other types
    setShowChatInput(false);
    setHideNextForText(false);
    setShrinkNext(false);
  }, [currentQuestionIndex, currentQuestion]);

  // Build a list of available language codes from sampleJson
  const allLanguages = useMemo(() => {
    const codesFromJson = Array.isArray(sampleJson.languageCodes)
      ? sampleJson.languageCodes
      : [];
    const combined = ["EN", ...codesFromJson];
    const unique = [...new Set(combined)]; // remove duplicates if any
    return unique;
  }, [sampleJson.languageCodes]);
  
  const totalQuestions = visibleQuestions.length;
  const currentQuestion = visibleQuestions[currentQuestionIndex];

  function handleNext() {
    setShowError(false);
    setErrorMessage("");
    
    // If no currentQuestion, do nothing
    if (!currentQuestion) return;

    // Validate response
    if (currentQuestion.type !== "information" && currentQuestion.type !== "consent") {
      const answerValue = answers[currentQuestion.uniqueId];
      if (!answerValue || answerValue.length === 0) {
        setShowError(true);
        setErrorMessage("Please answer this question before proceeding.");
        return;
      }
    }
    
    // For consent questions, ensure all required checkboxes are checked
    if (currentQuestion.type === "consent") {
      const blocks = currentQuestion.properties?.blocks || [];
      const requiredCheckboxes = blocks.filter(
        (block) => block.type === "checkbox" && block.required
      );

      const allRequiredChecked = requiredCheckboxes.every((checkbox) =>
        answers[currentQuestion.uniqueId]?.[checkbox.label]
      );

      if (!allRequiredChecked) {
        setShowError(true);
        setErrorMessage("Please check all required consent boxes to continue.");
        return;
      }
    }

    // For text questions, animate Next -> dot and show chat-style input
    if (currentQuestion.type === "text") {
      // For text, we rely on the chat-style input's send action
      return;
    }

    // Animation and transition for other types
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else {
        setCompleted(true);
      }
    }, 400);
  }

  function handleBack() {
    if (currentQuestionIndex > 0) {
      setShowError(false);
      setIsFadingOut(true);

      setTimeout(() => {
        setIsFadingOut(false);
        setCurrentQuestionIndex((prev) => prev - 1);
      }, 400);
    }
  }

  function canProceed() {
    if (!currentQuestion) return false;

    // Information questions can always proceed
    if (currentQuestion.type === "information") {
      return true;
    }

    // Consent questions require all checkboxes
    if (currentQuestion.type === "consent") {
      const blocks = currentQuestion.properties?.blocks || [];
      const requiredCheckboxes = blocks.filter(
        (block) => block.type === "checkbox" && block.required
      );

      return requiredCheckboxes.every(
        (checkbox) => answers[currentQuestion.uniqueId]?.[checkbox.label] === true
      );
    }

    // Other question types require a non-empty answer
    const answerValue = answers[currentQuestion.uniqueId];
    return answerValue && answerValue.length > 0;
  }

  function handleSendChat() {
    if (!currentQuestion) return;
    const value = chatDraft.trim();
    if (!value) return;
    // Save answer
    handleAnswerChange(currentQuestion.uniqueId, { response: value });
    // Reset chat and proceed with transition
    setShowChatInput(false);
    setShrinkNext(false);
    setChatDraft("");

    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else {
        setCompleted(true);
      }
    }, 400);
  }

  // Render the current question based on its type
  function renderQuestion(question) {
    const qAnswer = answers[question.uniqueId] || "";

    if(question?.aiDriven){
      return (
        <QuestionAiFollowUp
          question={question}
          questionList={visibleQuestions}
          answers={answers}
          surveyGoal={"We want to find the root cause of X..."}
          onAnswerChange={handleAnswerChange}
          factors={sampleJson.factors}
          onNext={handleNext}
          onBack={handleBack}
          showBackButton={currentQuestionIndex > 0}
          currentAnswer={qAnswer}
          id={question.uniqueId}
        />
      );
    }

    switch (question.type) {
      case "information":
      case "consent":
        return (
          <QuestionInformation
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      case "text":
        return (
          <QuestionText
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
            hideInput={true}
          />
        );
      case "slider":
        return (
          <QuestionSlider
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      case "likert":
        return (
          <QuestionLikert
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      case "multiSelect":
        return (
          <QuestionMultiSelect
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      case "dropdown":
        return (
          <QuestionDropdown
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      case "followup":
        return (
          <QuestionFollowup
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      case "factorFollowup":
        return (
          <FactorFollowUp
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      case "aggregateFactorFollowup":
        return (
          <FactorFollowUp
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      case "date":
        return (
          <QuestionDate
            question={question}
            currentAnswer={qAnswer}
            onAnswerChange={handleAnswerChange}
          />
        );
      default:
        return (
          <div>
            <p>Unknown type: {question.type}</p>
          </div>
        );
    }
  }

  return (
    <Container themeColors={themeColors}>
      <TopBanner themeColors={themeColors}>
        <Logo src={logo} alt="Logo" />
        <Language language={language} setLanguage={setLanguage} allLanguages={allLanguages} />
      </TopBanner>

      <Content>
        <AnimatedCard isFadingOut={isFadingOut}>
          {renderQuestion(currentQuestion)}

          {showError && (
            <RedError>{errorMessage}</RedError>
          )}
        </AnimatedCard>
      </Content>

      {/* Fixed bottom navigation bar */}
      <BottomBar>
        <ButtonWrapper>
          {currentQuestionIndex > 0 ? (
            <BackButton onClick={handleBack} themeColors={themeColors}>Back</BackButton>
          ) : (
            <div style={{ width: "100px" }}></div>
          )}
        </ButtonWrapper>

        <ProgressWrapper>
          <Progress>
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </Progress>
        </ProgressWrapper>

        <ButtonWrapper>
          {!hideNextForText || currentQuestion?.type !== "text" ? (
            <NextButtonWrapper isShrinking={shrinkNext && currentQuestion?.type === "text"}>
              <NextButton onClick={handleNext} active={canProceed()} themeColors={themeColors}>
                {currentQuestionIndex < totalQuestions - 1 ? "Next" : "Submit"}
              </NextButton>
            </NextButtonWrapper>
          ) : (
            <div style={{ width: "100px" }}></div>
          )}
        </ButtonWrapper>
      </BottomBar>

      {showChatInput && currentQuestion?.type === "text" && (
        <ChatInputContainer>
          <div style={{ position: "relative", width: "100%" }}>
            <ChatInput
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              placeholder="Type your answer..."
            />
            <SendButton themeColors={themeColors} onClick={handleSendChat} aria-label="Send">
              ➤
            </SendButton>
          </div>
        </ChatInputContainer>
      )}
    </Container>
  );
};

export default SurveyQuestions; 